import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createTextVNode as _createTextVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "self-start group bg-white/5 rounded-lg overflow-hidden transition-all duration-200 hover:border-teal-500/30 h-max-full min-h-[326px]" }
const _hoisted_2 = { class: "relative flex flex-col h-full" }
const _hoisted_3 = { class: "absolute top-3 right-3" }
const _hoisted_4 = { class: "p-4" }
const _hoisted_5 = { class: "flex items-start justify-between mb-2" }
const _hoisted_6 = { class: "font-semibold text-white text-lg truncate" }
const _hoisted_7 = {
  key: 0,
  class: "text-gray-400 text-sm line-clamp-2 mb-3 h-10"
}
const _hoisted_8 = { class: "p-4 pt-0 flex items-center justify-between items-self-end h-full" }
const _hoisted_9 = { class: "flex items-center gap-2" }
const _hoisted_10 = ["disabled"]
const _hoisted_11 = { class: "flex items-center gap-1" }
const _hoisted_12 = {
  key: 0,
  class: "p-4 border-t border-white/10"
}
const _hoisted_13 = {
  key: 0,
  class: "flex justify-center py-4"
}
const _hoisted_14 = { key: 1 }
const _hoisted_15 = {
  key: 2,
  class: "text-center text-gray-400 py-4"
}

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { Flow } from '@/interfaces/chat-flow/flow/Flow';
import { BarChart, GitBranch, Loader2, Pencil, ShoppingCart, Trash, TrendingDown, TrendingUp, Users, Workflow, Zap } from 'lucide-vue-next'
import ConversionBar from './ConversionBar.vue';
import FlowMetrics from './FlowMetrics.vue';
import ConversionFunnel from './ConversionFunnel.vue';
import { computed, ref } from 'vue';
import { BanknotesIcon } from '@heroicons/vue/24/outline';
import { Switch } from '@/components/ui/switch';
import StatusBadge from '@/components/ui/badge/StatusBadge.vue';
import { FlowStatusEnum } from '@/enums/chatFlow/flows/FlowStatusEnum'
import { FlowSummary, useFlow } from '@/composables/chatFlow/useFlow';


export default /*@__PURE__*/_defineComponent({
  __name: 'FlowCard',
  props: {
    flow: {}
  },
  emits: [
  'editFlow',
  'deleteFlow',
  'switchFlow'
],
  setup(__props: any) {



const props = __props

const { getSummary } = useFlow()

// Estado para controle do loading e exibição
const loadingSummary = ref(false)
const showMetrics = ref(false)
const summary = ref<FlowSummary | null>(null)

// Carrega o summary quando o botão é clicado
const loadSummary = async () => {
  showMetrics.value = !showMetrics.value
  
  if (showMetrics.value && !summary.value) {
    try {
      loadingSummary.value = true
      summary.value = await getSummary(props.flow.id)
    } catch (error) {
      console.error('Erro ao carregar métricas:', error)
    } finally {
      loadingSummary.value = false
    }
  }
}

// Calcula os dados com base no summary
const stats = computed(() => ({
  commission: summary.value?.totalCommission || 0,
  leads: summary.value?.leadsCount || 0,
  sales: summary.value?.paidOrdersCount || 0,
  upsells: summary.value?.upsellCount || 0,
  downsells: summary.value?.downsellCount || 0,
  hotLeads: Math.floor((summary.value?.leadsCount || 0) * 0.15) // Exemplo: 15% dos leads são quentes
}))

// Calculate conversion rate (sales / leads)
const conversionRate = computed(() => {
  if (stats.value.leads === 0) return '0.0'
  const rate = (stats.value.sales / stats.value.leads) * 100
  return rate.toFixed(1)
})

// Metrics for the cards
const metrics = computed(() => [
  {
    label: 'Total de Ganhos',
    value: stats.value.commission.toLocaleString('pt-BR', { currency: 'BRL', style: 'currency' }),
    icon: BanknotesIcon,
    change: 12.5 // Você pode calcular isso com base em dados históricos
  },
  {
    label: 'Total de Leads',
    value: stats.value.leads,
    icon: Users,
    change: 8.3
  },
  {
    label: 'Vendas',
    value: stats.value.sales,
    icon: ShoppingCart,
    change: 5.7
  },
  {
    label: 'Upsells',
    value: stats.value.upsells,
    icon: TrendingUp,
    change: 15.2
  },
  {
    label: 'Downsells',
    value: stats.value.downsells,
    icon: TrendingDown,
    change: -3.1
  },
  // {
  //   label: 'Leads Quentes',
  //   value: stats.value.hotLeads,
  //   icon: Zap,
  //   change: 10.0
  // }
])

// Funnel data
const funnel = computed(() => [
  {
    label: 'Leads',
    shortLabel: 'Leads',
    value: stats.value.leads,
    percentage: 100,
    color: 'bg-teal-700/50'
  },
  // {
  //   label: 'Leads Quentes',
  //   shortLabel: 'Quentes',
  //   value: stats.value.hotLeads,
  //   percentage: stats.value.leads > 0 ? Math.round((stats.value.hotLeads / stats.value.leads) * 100) : 0,
  //   color: 'bg-teal-600/60'
  // },
  {
    label: 'Vendas',
    shortLabel: 'Vendas',
    value: stats.value.sales,
    percentage: stats.value.leads > 0 ? Math.round((stats.value.sales / stats.value.leads) * 100) : 0,
    color: 'bg-teal-500/70'
  },
  {
    label: 'Upsells',
    shortLabel: 'Upsells',
    value: stats.value.upsells,
    percentage: stats.value.leads > 0 ? Math.round((stats.value.upsells / stats.value.leads) * 100) : 0,
    color: 'bg-teal-400/80'
  },
  {
    label: 'Downsells',
    shortLabel: 'Downsells',
    value: stats.value.downsells,
    percentage: stats.value.leads > 0 ? Math.round((stats.value.downsells / stats.value.leads) * 100) : 0,
    color: 'bg-teal-300/90'
  }
])

return (_ctx: any,_cache: any) => {
  const _component_RouterLink = _resolveComponent("RouterLink")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", {
        onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$router.push({ name: 'flows.show', params: { flowId: _ctx.flow.id } }))),
        class: "h-40 flex items-center justify-center bg-gradient-to-br from-teal-900/20 to-transparent cursor-pointer"
      }, [
        _createVNode(_unref(GitBranch), { class: "h-16 w-16 text-teal-400/30" })
      ]),
      _createElementVNode("div", _hoisted_3, [
        _createVNode(StatusBadge, {
          status: _ctx.flow.status
        }, null, 8, ["status"])
      ])
    ]),
    _createElementVNode("div", _hoisted_4, [
      _createElementVNode("div", _hoisted_5, [
        _createElementVNode("h3", _hoisted_6, _toDisplayString(_ctx.flow.name), 1)
      ]),
      (_ctx.flow.description)
        ? (_openBlock(), _createElementBlock("p", _hoisted_7, _toDisplayString(_ctx.flow.description), 1))
        : _createCommentVNode("", true)
    ]),
    _createElementVNode("div", _hoisted_8, [
      _createElementVNode("div", _hoisted_9, [
        _createVNode(_unref(TooltipProvider), null, {
          default: _withCtx(() => [
            _createVNode(_unref(Tooltip), { "delay-duration": 100 }, {
              default: _withCtx(() => [
                _createVNode(_unref(TooltipTrigger), { "as-child": "" }, {
                  default: _withCtx(() => [
                    _createVNode(_component_RouterLink, {
                      to: { name: 'flows.show', params: { flowId: _ctx.flow.id } },
                      size: "sm",
                      class: "text-teal-400 hover:text-teal-300 hover:bg-teal-900/20 flex items-center gap-1 bg-white/5 p-2 rounded-sm"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_unref(Workflow), { class: "h-3.5 w-3.5" })
                      ]),
                      _: 1
                    }, 8, ["to"])
                  ]),
                  _: 1
                }),
                _createVNode(_unref(TooltipContent), {
                  side: "top",
                  "side-offset": 5
                }, {
                  default: _withCtx(() => _cache[4] || (_cache[4] = [
                    _createTextVNode(" Fluxo de mensagens ")
                  ])),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          _: 1
        }),
        _createVNode(_unref(TooltipProvider), null, {
          default: _withCtx(() => [
            _createVNode(_unref(Tooltip), { "delay-duration": 100 }, {
              default: _withCtx(() => [
                _createVNode(_unref(TooltipTrigger), { "as-child": "" }, {
                  default: _withCtx(() => [
                    _createElementVNode("button", {
                      size: "sm",
                      class: "text-teal-400 hover:text-teal-300 hover:bg-teal-900/20 flex items-center gap-1 bg-white/5 p-2 rounded-sm",
                      onClick: loadSummary,
                      disabled: loadingSummary.value
                    }, [
                      (loadingSummary.value)
                        ? (_openBlock(), _createBlock(_unref(Loader2), {
                            key: 0,
                            class: "h-3.5 w-3.5 animate-spin"
                          }))
                        : (_openBlock(), _createBlock(_unref(BarChart), {
                            key: 1,
                            class: "h-3.5 w-3.5"
                          }))
                    ], 8, _hoisted_10)
                  ]),
                  _: 1
                }),
                _createVNode(_unref(TooltipContent), {
                  side: "top",
                  "side-offset": 5
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(showMetrics.value ? 'Ocultar métricas' : 'Ver métricas'), 1)
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          _: 1
        }),
        _createVNode(_unref(TooltipProvider), null, {
          default: _withCtx(() => [
            _createVNode(_unref(Tooltip), { "delay-duration": 100 }, {
              default: _withCtx(() => [
                _createVNode(_unref(TooltipTrigger), { "as-child": "" }, {
                  default: _withCtx(() => [
                    _createElementVNode("button", {
                      class: "text-teal-400 hover:text-teal-300 hover:bg-teal-900/20 flex items-center gap-1 bg-white/5 p-2 rounded-sm",
                      onClick: _cache[1] || (_cache[1] = ($event: any) => (_ctx.$emit('editFlow', _ctx.flow)))
                    }, [
                      _createVNode(_unref(Pencil), { class: "h-3.5 w-3.5" })
                    ])
                  ]),
                  _: 1
                }),
                _createVNode(_unref(TooltipContent), {
                  side: "top",
                  "side-offset": 5
                }, {
                  default: _withCtx(() => _cache[5] || (_cache[5] = [
                    _createTextVNode(" Editar ")
                  ])),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          _: 1
        }),
        _createVNode(_unref(TooltipProvider), null, {
          default: _withCtx(() => [
            _createVNode(_unref(Tooltip), { "delay-duration": 100 }, {
              default: _withCtx(() => [
                _createVNode(_unref(TooltipTrigger), { "as-child": "" }, {
                  default: _withCtx(() => [
                    _createElementVNode("button", {
                      class: "text-teal-400 hover:text-teal-300 hover:bg-teal-900/20 flex items-center gap-1 bg-white/5 p-2 rounded-sm",
                      onClick: _cache[2] || (_cache[2] = ($event: any) => (_ctx.$emit('deleteFlow', _ctx.flow)))
                    }, [
                      _createVNode(_unref(Trash), { class: "h-3.5 w-3.5" })
                    ])
                  ]),
                  _: 1
                }),
                _createVNode(_unref(TooltipContent), {
                  side: "top",
                  "side-offset": 5
                }, {
                  default: _withCtx(() => _cache[6] || (_cache[6] = [
                    _createTextVNode(" Excluir ")
                  ])),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _createElementVNode("div", _hoisted_11, [
        _createVNode(_unref(Switch), {
          checked: _ctx.flow.status === _unref(FlowStatusEnum).ACTIVE,
          "onUpdate:checked": _cache[3] || (_cache[3] = ($event: any) => (_ctx.$emit('switchFlow', _ctx.flow)))
        }, null, 8, ["checked"])
      ])
    ]),
    (showMetrics.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
          (loadingSummary.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                _createVNode(_unref(Loader2), { class: "h-6 w-6 animate-spin text-teal-400" })
              ]))
            : (summary.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                  _createVNode(ConversionBar, {
                    conversionRate: Number(conversionRate.value)
                  }, null, 8, ["conversionRate"]),
                  _createVNode(FlowMetrics, { metrics: metrics.value }, null, 8, ["metrics"])
                ]))
              : (_openBlock(), _createElementBlock("div", _hoisted_15, " Nenhum dado disponível "))
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})